// Relatórios — dashboard executivo do Reportei (IG + LinkedIn agregados).
// Antes essa página fazia contagens internas de pautas/textos/peças. Refeito
// pra puxar dados reais de redes sociais via Reportei e ser o "resumo do mês"
// que o time entrega pro cliente.

import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

function useActiveClientId(): number | undefined {
  const { session } = useAuth();
  return session?.activeClientId ?? session?.clientId ?? undefined;
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".", ",") + "mi";
  if (abs >= 1_000) return (n / 1_000).toFixed(1).replace(".", ",") + "mil";
  return String(Math.round(n));
}
function fmtPct(n: number | null | undefined): string {
  return n == null ? "—" : n.toFixed(1).replace(".", ",") + "%";
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-[var(--solar-mute)] mb-1.5">{label}</p>
      <p className="text-2xl num-hero">{value}</p>
      {sub && <p className="text-[11px] text-[var(--solar-mute)] mt-0.5">{sub}</p>}
    </div>
  );
}

function RedeBlock({
  titulo, data, loading, error, acentColor,
}: {
  titulo: string; data: any; loading: boolean; error: any; acentColor: string;
}) {
  const isIg = titulo === "Instagram";
  const kpis = !data ? [] : isIg
    ? [
        { label: "Seguidores",       value: fmtNum(data.currentFollowers) },
        { label: "Novos no período", value: fmtNum(data.followersTotal) },
        { label: "Alcance total",    value: fmtNum(data.reachTotal) },
        { label: "Alcance orgânico", value: fmtNum(data.organicReach) },
      ]
    : [
        { label: "Seguidores",       value: fmtNum(data.liFollowersTotal) },
        { label: "Novos no período", value: fmtNum(data.liNewFollowers) },
        { label: "Impressões",       value: fmtNum(data.impressionsTotal) },
        { label: "Taxa de engaj.",   value: fmtPct(data.engagementRate) },
      ];

  return (
    <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 rounded-full" style={{ background: acentColor }} />
        <h3 className="font-bold text-sm text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{titulo}</h3>
      </div>

      {loading && <p className="text-sm text-[var(--solar-mute)] italic">Carregando…</p>}
      {error && (
        <p className="text-sm text-red-600">
          {error.message?.includes("não configurado")
            ? "Reportei não configurado pra este cliente — Admin → Integrações → Reportei"
            : error.message ?? "Erro ao carregar"}
        </p>
      )}
      {!loading && !error && !data && <p className="text-sm text-[var(--solar-mute)] italic">Sem dados pro período.</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {kpis.map(k => (
              <div key={k.label}>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[var(--solar-mute)] mb-1">{k.label}</p>
                <p className="text-xl font-bold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{k.value}</p>
              </div>
            ))}
          </div>
          <div className="text-xs text-[var(--solar-mute)] pt-3 border-t border-[var(--solar-line)]">
            {data.posts?.length ?? 0} posts no período
          </div>
        </>
      )}
    </div>
  );
}

export default function Relatorios() {
  const clientId = useActiveClientId();
  const [dias, setDias] = useState(30);

  const igQ = trpc.reportei.dashboard.useQuery(
    { rede: "instagram", dias, clientId },
    { retry: false, refetchOnWindowFocus: false, enabled: clientId != null }
  );
  const liQ = trpc.reportei.dashboard.useQuery(
    { rede: "linkedin", dias, clientId },
    { retry: false, refetchOnWindowFocus: false, enabled: clientId != null }
  );

  const topPosts = useMemo(() => {
    const ig = (igQ.data?.posts ?? []).map((p: any) => ({ ...p, rede: "Instagram" }));
    const li = (liQ.data?.posts ?? []).map((p: any) => ({ ...p, rede: "LinkedIn" }));
    return [...ig, ...li]
      .sort((a, b) => (b.performanceScore ?? 0) - (a.performanceScore ?? 0))
      .slice(0, 10);
  }, [igQ.data, liQ.data]);

  const aggregate = useMemo(() => {
    const ig = igQ.data; const li = liQ.data;
    return {
      totalFollowers: (ig?.currentFollowers ?? 0) + (li?.liFollowersTotal ?? 0),
      newFollowers:   (ig?.followersTotal ?? 0)   + (li?.liNewFollowers ?? 0),
      reach:          ig?.reachTotal ?? 0,
      impressions:    li?.impressionsTotal ?? 0,
    };
  }, [igQ.data, liQ.data]);

  const periodLabel =
    dias === 7  ? "últimos 7 dias" :
    dias === 15 ? "últimas 2 semanas" :
    dias === 30 ? "últimos 30 dias" :
    dias === 60 ? "últimos 2 meses" :
    dias === 90 ? "últimos 90 dias" :
    `últimos ${dias} dias`;

  return (
    <div className="h-full overflow-y-auto bg-[var(--solar-bg)]">
      <div className="px-6 py-5 sticky top-0 z-10 bg-white border-b border-[var(--solar-line)]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-bold text-xl text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>Relatórios</h1>
            <p className="text-[var(--solar-mute)] text-xs mt-0.5">Resumo executivo · Instagram + LinkedIn via Reportei · {periodLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--solar-mute)]">Período</span>
            <select value={dias} onChange={e => setDias(Number(e.target.value))}
              className="text-sm rounded-lg border border-[var(--solar-line)] bg-white px-3 py-1.5">
              <option value={7}>Últimos 7 dias</option>
              <option value={15}>Últimas 2 semanas</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={60}>Últimos 2 meses</option>
              <option value={90}>Últimos 90 dias</option>
            </select>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-6xl mx-auto space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Seguidores totais"  value={fmtNum(aggregate.totalFollowers)} sub="IG + LinkedIn" />
          <KpiCard label="Novos no período"   value={fmtNum(aggregate.newFollowers)}   sub={periodLabel} />
          <KpiCard label="Alcance IG"         value={fmtNum(aggregate.reach)}          sub="Instagram" />
          <KpiCard label="Impressões LI"      value={fmtNum(aggregate.impressions)}    sub="LinkedIn" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RedeBlock titulo="Instagram" data={igQ.data} loading={igQ.isLoading} error={igQ.error} acentColor="#BE185D" />
          <RedeBlock titulo="LinkedIn"  data={liQ.data} loading={liQ.isLoading} error={liQ.error} acentColor="#1E40AF" />
        </div>

        {topPosts.length > 0 && (
          <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
            <h3 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
              Top 10 posts — performance no período
            </h3>
            <div className="space-y-2">
              {topPosts.map((p: any, i: number) => (
                <a key={`${p.rede}-${p.id ?? i}`} href={p.url || "#"} target="_blank" rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-[var(--solar-bg)] transition-colors border border-transparent hover:border-[var(--solar-line)]">
                  <div className="w-7 h-7 rounded-full bg-[var(--solar-bg)] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                        style={{
                          background: p.rede === "Instagram" ? "#FDE8F5" : "#DBEAFE",
                          color:      p.rede === "Instagram" ? "#BE185D" : "#1E40AF",
                        }}>{p.rede}</span>
                      <span className="text-[10px] text-[var(--solar-mute)]">
                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : ""}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--solar-ink)] line-clamp-2 break-words">{p.text || "(sem texto)"}</p>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-[var(--solar-mute)]">
                      {p.reach != null    && <span>Alcance {fmtNum(p.reach)}</span>}
                      {p.likes != null    && <span>Curtidas {fmtNum(p.likes)}</span>}
                      {p.comments != null && <span>Comentários {fmtNum(p.comments)}</span>}
                      {p.engagementRate != null && <span>Eng. {fmtPct(p.engagementRate)}</span>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {!igQ.isLoading && !liQ.isLoading && !igQ.data && !liQ.data && !igQ.error && !liQ.error && (
          <div className="rounded-xl border border-dashed border-[var(--solar-line)] bg-white p-8 text-center">
            <p className="text-sm font-semibold text-[var(--solar-ink)] mb-1">Sem dados de Reportei</p>
            <p className="text-xs text-[var(--solar-mute)]">Configure em <strong>Admin → Integrações → Reportei</strong>.</p>
          </div>
        )}
      </div>
    </div>
  );
}
